const FIRST_PARTY_AUTH_HOSTS = /^(app|dev|preview)\.braiv\.co$/i;
const FIREBASE_HOSTED_AUTH_DOMAIN = /\.(firebaseapp\.com|web\.app)$/i;

function stripAuthHost(value: string | null | undefined): string {
    return String(value || "")
        .trim()
        .replace(/^https?:\/\//i, "")
        .replace(/\/+$/, "")
        .split("/")[0] || "";
}

export function isFirstPartyFirebaseAuthHost(hostname: string | null | undefined): boolean {
    return FIRST_PARTY_AUTH_HOSTS.test(String(hostname || "").trim().toLowerCase());
}

/**
 * Google OAuth allowlists https://auth.<env>.braiv.co/__/auth/handler
 * (see NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN). Do not swap that for the app
 * host — https://app.braiv.co/__/auth/handler is a redirect_uri_mismatch.
 */
export function resolveFirebaseAuthDomain(input: {
    hostname?: string | null;
    configuredAuthDomain?: string | null;
} = {}): string {
    const configured = stripAuthHost(input.configuredAuthDomain);
    if (configured) {
        return configured;
    }
    const hostname = stripAuthHost(
        input.hostname ?? (typeof window === "undefined" ? "" : window.location.hostname),
    );
    if (isFirstPartyFirebaseAuthHost(hostname)) {
        return `auth.${hostname}`;
    }
    return configured;
}

/**
 * Proxy target for /__/auth and /__/firebase. Must be the Firebase-hosted
 * helper origin, never the app host (that would loop the rewrite).
 */
export function resolveFirebaseAuthProxyOrigin(input: {
    configuredAuthDomain?: string | null;
    projectId?: string | null;
} = {}): string | null {
    const configured = stripAuthHost(input.configuredAuthDomain);
    if (FIREBASE_HOSTED_AUTH_DOMAIN.test(configured)) {
        return `https://${configured}`;
    }
    const projectId = String(input.projectId || "").trim();
    if (projectId) {
        return `https://${projectId}.firebaseapp.com`;
    }
    return null;
}

export function buildFirebaseAuthProxyRewrites(proxyOrigin: string | null): Array<{
    source: string;
    destination: string;
}> {
    if (!proxyOrigin) {
        return [];
    }
    const origin = proxyOrigin.replace(/\/+$/, "");
    return [
        {
            source: "/__/auth/:path*",
            destination: `${origin}/__/auth/:path*`,
        },
        {
            source: "/__/firebase/:path*",
            destination: `${origin}/__/firebase/:path*`,
        },
    ];
}

export function resolveGoogleRedirectOutcome(input: {
    redirectUser?: {uid: string} | null;
    currentUser?: {uid: string} | null;
    redirectWasPending: boolean;
}): "credential" | "existing-session" | "incomplete" | "idle" {
    if (input.redirectUser?.uid) {
        return "credential";
    }
    if (input.redirectWasPending && input.currentUser?.uid) {
        return "existing-session";
    }
    if (input.redirectWasPending) {
        return "incomplete";
    }
    return "idle";
}
